import * as React from 'react';

import {
  DocumentType,
  enumDocumentType,
} from '@rematter/frontend-lib-api-client';

import { LoadRecord } from '~/modules/trade/types/load';

const DOCUMENT_TYPES = [
  enumDocumentType.BillOfLading,
  enumDocumentType.Invoice,
  enumDocumentType.PackingList,
];

export const useCheckboxes = (
  loads: (Pick<Partial<LoadRecord>, 'loadNumber' | 'invoiceId'> & {
    id: string;
  })[],
) => {
  const handleLoadSelect = React.useCallback(
    (loadId: string, checked: boolean) =>
      (prevState: Record<string, Record<DocumentType, boolean>>) => {
        const newState = { ...prevState };
        const load = loads.find((l) => l.id === loadId);

        if (checked) {
          // Select all document types for this load, but only invoice if load has invoiceId
          newState[loadId] = DOCUMENT_TYPES.reduce(
            (acc, type) => {
              if (type === enumDocumentType.Invoice && !load?.invoiceId) {
                // Skip invoice if load doesn't have invoiceId
                return acc;
              }
              acc[type] = true;
              return acc;
            },
            {} as Record<DocumentType, boolean>,
          );
        } else {
          // Deselect all document types for this load
          delete newState[loadId];
        }

        return newState;
      },
    [loads],
  );

  const handleDocumentSelect = React.useCallback(
    (loadId: string, documentType: DocumentType, checked: boolean) =>
      (prevState: Record<string, Record<DocumentType, boolean>>) => {
        const newState = { ...prevState };
        const load = loads.find((l) => l.id === loadId);

        // Don't allow selecting invoice if load doesn't have invoiceId
        if (documentType === enumDocumentType.Invoice && !load?.invoiceId) {
          return prevState;
        }

        if (!newState[loadId]) {
          newState[loadId] = {} as Record<DocumentType, boolean>;
        }

        if (checked) {
          newState[loadId][documentType] = true;
        } else {
          delete newState[loadId][documentType];

          // If no documents are selected for this load, remove the load entry
          if (Object.keys(newState[loadId]).length === 0) {
            delete newState[loadId];
          }
        }

        return newState;
      },
    [loads],
  );

  const handleHeaderSelect = React.useCallback(
    (documentType: DocumentType | 'all', checked: boolean) =>
      (prevState: Record<string, Record<DocumentType, boolean>>) => {
        const newState = { ...prevState };

        if (documentType === 'all') {
          if (checked) {
            // Select all documents for all loads, but only invoice for loads with invoiceId
            loads.forEach((load) => {
              newState[load.id] = DOCUMENT_TYPES.reduce(
                (acc, type) => {
                  if (type === enumDocumentType.Invoice && !load.invoiceId) {
                    // Skip invoice if load doesn't have invoiceId
                    return acc;
                  }
                  acc[type] = true;
                  return acc;
                },
                {} as Record<DocumentType, boolean>,
              );
            });
          } else {
            // Deselect all documents
            return {};
          }
        } else {
          if (checked) {
            // Select this document type for all loads, but only invoice for loads with invoiceId
            loads.forEach((load) => {
              if (
                documentType === enumDocumentType.Invoice &&
                !load.invoiceId
              ) {
                // Skip invoice if load doesn't have invoiceId
                return;
              }

              if (!newState[load.id]) {
                newState[load.id] = {} as Record<DocumentType, boolean>;
              }
              newState[load.id][documentType] = true;
            });
          } else {
            // Deselect this document type for all loads
            loads.forEach((load) => {
              if (newState[load.id]) {
                delete newState[load.id][documentType];

                // If no documents are selected for this load, remove the load entry
                if (Object.keys(newState[load.id]).length === 0) {
                  delete newState[load.id];
                }
              }
            });
          }
        }

        return newState;
      },
    [loads],
  );

  return {
    handleLoadSelect,
    handleDocumentSelect,
    handleHeaderSelect,
  };
};
